import { createContext, useEffect, useRef, useState } from "react"

import useUser from "@hooks/useUser"

import getImageType from "@utils/getImageType"

import HiddenImageInput from "@molecules/Form/inputs/HiddenImageInput/HiddenImageInput"
import { HiddenImageInputRef, ImageDimensions } from "@molecules/Form/inputs/HiddenImageInput/HiddenImageInput.types"
import toast from "@molecules/Toast/Toast"

import AvatarCropDialog from "@pages/Settings/SettingsProfile/components/AvatarCropDialog/AvatarCropDialog"

import AvatarCard from "./AvatarCard/AvatarCard"
import styles from "./AvatarManagement.module.scss"
import { useRemoveAvatarMutation, useUpdateAvatarMutation } from "./AvatarManagement.store"
import { AvatarFormatType, AvatarManagementContextValues } from "./AvatarManagement.types"
import { validateImageProperties } from "./AvatarManagement.utils"

export const AvatarManagementContext = createContext<AvatarManagementContextValues | undefined>(undefined)
AvatarManagementContext.displayName = "AvatarManagement"

export default function AvatarManagement() {
    const { user, updateUser } = useUser()

    const [displayedAvatar, setDisplayedAvatar] = useState<string>("")
    const [imageToCrop, setImageToCrop] = useState<string>(displayedAvatar)
    const [imageFormatType, setImageFormatType] = useState<AvatarFormatType>()
    const [userHasUploadedAvatar, setUserHasUploadedAvatar] = useState<boolean>(false)
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

    const fileInputRef = useRef<HiddenImageInputRef>(null)

    const {
        mutate: updateAvatarMutation,
        isError: isErrorOnUpload,
        isPending: isUploadingImage,
        isSuccess: isImageUploaded,
    } = useUpdateAvatarMutation()

    const {
        mutate: deleteUserAvatarMutation,
        isError: isErrorOnDelete,
        isSuccess: isImageRemoved,
    } = useRemoveAvatarMutation()

    useEffect(() => {
        if (isImageRemoved) {
            updateUser({ avatar: undefined })
            setDisplayedAvatar(user?.gravatar ?? "")
            setImageToCrop("")
            setUserHasUploadedAvatar(false)

            toast({
                type: "success",
                variant: "default",
                title: "Avatar removed",
            })
        }
    }, [isImageRemoved])

    useEffect(() => {
        if (isErrorOnDelete) {
            toast({
                type: "error",
                variant: "default",
                title: "Could not remove avatar",
            })
        }
    }, [isErrorOnDelete])

    useEffect(() => {
        if (isImageUploaded) {
            updateUser({ avatar: displayedAvatar })
            setUserHasUploadedAvatar(true)

            toast({
                type: "success",
                variant: "default",
                title: "Avatar uploaded",
            })
        }
    }, [isImageUploaded])

    useEffect(() => {
        if (isErrorOnUpload) {
            setDisplayedAvatar("")

            toast({
                type: "error",
                variant: "default",
                title: "Could not upload avatar",
            })
        }
    }, [isErrorOnUpload])

    useEffect(() => {
        const fetchAvatar = async () => {
            if (user) {
                const avatarUrl = user.avatar || user.gravatar
                setUserHasUploadedAvatar(!!user.avatar)

                if (avatarUrl) {
                    try {
                        const response = await fetch(avatarUrl)
                        if (response.ok) {
                            setDisplayedAvatar(response.url)
                            const imageType = await getImageType(response.url)
                            setImageFormatType(imageType as AvatarFormatType)
                        }
                    } catch (error) {
                        console.log("Error getting image type:", error)
                    }
                }
            }
        }
        void fetchAvatar()
    }, [user?.id])

    useEffect(() => {
        return () => {
            if (displayedAvatar) {
                URL.revokeObjectURL(displayedAvatar)
            }

            if (imageToCrop) {
                URL.revokeObjectURL(imageToCrop)
            }
        }
    }, [])

    const handleFileChange = (image: File, imageDimensions: ImageDimensions) => {
        const fileFormat = image.type as AvatarFormatType

        const validationResult = validateImageProperties(image, imageDimensions)

        if (validationResult.valid) {
            setImageFormatType(fileFormat)

            updateAvatarMutation({
                avatar: image,
            })

            const objectUrl = URL.createObjectURL(image)

            setImageToCrop(objectUrl)
            setDisplayedAvatar(objectUrl)

            setIsDialogOpen(true)
        } else {
            toast({
                type: "error",
                variant: "default",
                title: validationResult.message,
            })
        }

        fileInputRef.current?.resetInput()
    }

    const handleDeleteAvatar = () => {
        deleteUserAvatarMutation()
    }

    const selectImageFromOS = () => {
        fileInputRef.current?.selectImageFromOS()
    }

    return (
        <AvatarManagementContext.Provider
            value={{
                imageToCrop,
                displayedAvatar,
                imageFormatType,
                isUploadingImage,
                userHasUploadedAvatar,
                setImageToCrop,
                selectImageFromOS,
                setDisplayedAvatar,
                setImageFormatType,
                updateAvatarMutation,
                deleteUserAvatarMutation,
                isDialogOpen,
                setIsDialogOpen,
                handleDeleteAvatar,
            }}
        >
            <div className={styles.base}>
                <HiddenImageInput onFileChange={handleFileChange} ref={fileInputRef} />
                <AvatarCard />
                <AvatarCropDialog />
            </div>
        </AvatarManagementContext.Provider>
    )
}
